import { ChangeEvent, FormEvent, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Form,
  FormContext,
  FormProvider,
  Text,
  ViewerContext,
  toast,
  useServerResponseTranslation,
} from 'dash-ui';

import { FormFields, FormFieldsList } from './AccountInfo.constants';
import useValidation from './AccountInfo.hooks';
import {
  useUpdateCurrentUserMutation,
  GetFullAccountDocument,
} from 'gql/generated.graphql';

function AccountInfoForm() {
  const { t } = useTranslation(['pages', 'forms']);
  const formRef = useRef<HTMLFormElement>(null);

  const { profile } = useContext(ViewerContext);
  const {
    formState,
    stateHandlers,
    formErrors,
    resetForm,
    resetFormErrors,
    setDefaultFormValues,
  } = useContext(FormContext);

  const validate = useValidation();
  const translateErrorMessage = useServerResponseTranslation();

  const [updateCurrentUser] = useUpdateCurrentUserMutation({
    refetchQueries: [GetFullAccountDocument],
  });

  useEffect(() => {
    if (profile) {
      setDefaultFormValues({
        firstName: profile.firstName,
        lastName: profile.lastName,
        avatarFileId: profile.avatarFile?._id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();

    updateCurrentUser({
      variables: {
        profileInput: {
          firstName: formState.firstName,
          lastName: formState.lastName,
          avatarFileId: formState.avatarFileId,
        },
      },
      onCompleted: () => {
        toast.success(t('common:alerts.accountIsUpdated'));
      },
      onError: (error: any) => {
        toast.error(translateErrorMessage(error));
      },
    });
  };

  const onInputChange =
    (fieldName: FormFields) => (e: ChangeEvent<HTMLInputElement>) => {
      stateHandlers[fieldName](e.target.value);
      validate(fieldName, e.target.value);
    };

  return (
    <Form w="100%" ref={formRef} onSubmit={onFormSubmit}>
      <Flex flexDirection="column" w={635}>
        <Flex justifyContent="space-between" w="100%">
          <Form.Group mt={4} w={307}>
            <Form.Label md required>
              {t('forms:labels.firstName')}
            </Form.Label>
            <Form.Input
              required
              placeholder={t('forms:labels.firstName') || ''}
              w="100%"
              value={formState.firstName}
              onChange={onInputChange(FormFields.FirstName)}
              $errorType={formErrors[FormFields.FirstName]?.type}
            />
            <Form.Feedback $errorType={formErrors[FormFields.FirstName]?.type}>
              {formErrors[FormFields.FirstName]?.message}
            </Form.Feedback>
          </Form.Group>
          <Form.Group mt={4} w={307}>
            <Form.Label md required>
              {t('forms:labels.lastName')}
            </Form.Label>
            <Form.Input
              required
              placeholder={t('forms:labels.lastName') || ''}
              w="100%"
              value={formState.lastName}
              onChange={onInputChange(FormFields.LastName)}
              $errorType={formErrors[FormFields.LastName]?.type}
            />
            <Form.Feedback $errorType={formErrors[FormFields.LastName]?.type}>
              {formErrors[FormFields.LastName]?.message}
            </Form.Feedback>
          </Form.Group>
        </Flex>
      </Flex>
      <Flex w={224} mt={4} justifyContent="space-between">
        <Button
          w={108}
          type="button"
          disabled={
            profile?.firstName === formState.firstName &&
            profile?.lastName === formState.lastName
          }
          onClick={() => {
            if (formRef.current) {
              formRef.current.requestSubmit();
            }
          }}
        >
          {t('common:labels.save')}
        </Button>
        <Button
          w={108}
          variation="outline"
          type="reset"
          onClick={() => {
            resetForm();
            resetFormErrors();
          }}
        >
          {t('common:labels.cancel')}
        </Button>
      </Flex>
    </Form>
  );
}

function AccountInfo() {
  const { t } = useTranslation(['pages']);

  return (
    <Flex flexDirection="column">
      <Text semibold>{t('pages:profilePage.labels.accountInfo')}</Text>
      <FormProvider fieldsList={FormFieldsList}>
        <AccountInfoForm />
      </FormProvider>
    </Flex>
  );
}

export default AccountInfo;
